import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Modal, Paper, Fade, IconButton, Box, Button } from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Campaign as CampaignIcon, 
} from "@mui/icons-material";
import { URL } from "../config";
import axios from "axios";
import toast from "react-hot-toast";
import AdvertisementModal from "./AddAdvertisement";

const AdvertisementList = () => {
  const [dataSource, setDataSource] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editAd, setEditAd] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);


  const fetchAdvertisements = async () => {
    try {
      const res = await axios.get(`${URL}/advertisements`);
      const formattedData = res.data.map((item) => ({
        ...item,
        adImage: item.advertisementImage ? `${URL}${item.advertisementImage}` : "",
      }));
      setDataSource(formattedData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch advertisements");
    }
  };

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const handleEdit = (ad) => {
    setEditAd(ad);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this advertisement?")) return;
    try {
      await axios.delete(`${URL}/advertisements/${id}`);
      toast.success("Advertisement deleted successfully!");
      fetchAdvertisements();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete advertisement");
    }
  };

  const handleOpenImage = (imgUrl) => {
    setSelectedImage(imgUrl);
    setOpenImageModal(true);
  };
  const handleCloseImage = () => {
    setSelectedImage(null);
    setOpenImageModal(false);
  };


  const columns = useMemo(() => [
    {
      accessorKey: "actions",
      header: "Actions",
      size: 100,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon sx={{ color: "#174253" }} fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(row.original.advertisementId)}>
            <DeleteIcon sx={{ color: "#d32f2f" }} fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    { accessorKey: "advertisementId", header: "ID", size: 60 },
    {
      accessorKey: "adImage",
      header: "Image",
      size: 80,
      Cell: ({ row }) =>
        row.original.adImage ? (
          <img
            src={row.original.adImage}
            alt="ad"
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid #ccc",
              cursor: "pointer",
            }}
            onClick={() => handleOpenImage(row.original.adImage)}
            onError={(e) => (e.currentTarget.src = "/fallback-image.png")}
          />
        ) : (
          <Box
            sx={{
              width: 60,
              height: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: "#f0f0f0",
            }}
          >
            <CampaignIcon sx={{ color: "#888" }} />
          </Box>
        ),
    },
  ], []);

  return (
    <Box sx={{ width: "100%", bgcolor: "#ffffff", marginTop: 2, p: { xs: 1, md: 0 }, overflow: "hidden" }}>
   
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pr: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: 22, color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", pl: 1.5 }}>
          Advertisement List
        </Box>
        <Button
          sx={{
            borderRadius: 25,
            background: "linear-gradient(90deg, #0b8793, #174253)",
            color: "white",
            "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" },
          }}
          onClick={() => {
            setEditAd(null);
            setModalVisible(true);
          }}
        >
          Add Advertisement
        </Button>
      </Box>


      <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
        <MaterialReactTable
          columns={columns}
          data={dataSource}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnFilters={false}
          enableHiding={false}
          enableGlobalFilter={false}
          muiTablePaperProps={{ sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" } }}
          muiTableContainerProps={{ sx: { backgroundColor: "#fff", width: "100%", overflowX: "auto", borderRadius: "12px" } }}
          muiTableHeadCellProps={{ sx: { backgroundColor: "#e0e0e0ff", color: "#393e4f", fontWeight: 600, fontSize: "16px", borderBottom: "0.5px solid #e3e5eb", textAlign: "left", height: "63px", paddingTop: "23px" } }}
          muiTableBodyRowProps={{ sx: { backgroundColor: "#fff", "&:hover": { backgroundColor: "#eff6fe" }, "& td": { fontSize: { xs: "12px", sm: "14px" }, p: "8px", borderBottom: "0.5px solid #e3e5eb", height: "54px" } } }}
          initialState={{ pagination: { pageIndex: 0, pageSize: 10 } }}
        />
      </Box>

 
      <Modal
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        closeAfterTransition
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <Fade in={modalVisible}>
          <Paper sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "transparent", outline: "none" }}>
            <AdvertisementModal
              ad={editAd}
              onClose={() => {
                setModalVisible(false);
                fetchAdvertisements();
              }}
            />
          </Paper>
        </Fade>
      </Modal>

      <Modal
        open={openImageModal}
        onClose={handleCloseImage}
        closeAfterTransition
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <Fade in={openImageModal}>
          <Paper sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "transparent", outline: "none" }}>
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={handleCloseImage}
                sx={{ position:"absolute", top:8, right:8, color:"#000", zIndex:10 }}
              >
                <CloseIcon />
              </IconButton>

              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="preview"
                  style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8 }}
                />
              )}
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default AdvertisementList;
